import React, { useState } from "react";
import { Box, Breadcrumbs, Divider, Grid2, Link, Typography, IconButton } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { isEqual } from "lodash";

import AppLoader from "../components/AppLoader";
import ContentWrapper from "../components/ContentWrapper";
import ExportCsv from "../components/ExportCsv";
import FilterNav from "../components/FilterNav";
import NoResults from "../components/NoResults";
import PageHeading from "../components/PageHeading";
import SaveSearch from "../components/SaveSearch";
import TableAdvanced from "../components/TableAdvanced";
import { spectrumPricingCells } from "../constants/tables";
import allRoutes from "../constants/routes";
import { useGetSpectrumPricingQuery, useLazyExportSpectrumPricingQuery } from "../store/apis/search";
import { selectSpectrumPricingSlice, setPage, setRowsPerPage, setSortBy, setSavedValues } from "../store/slices/spectrumPricing";
import { selectUiState, setDense, setFontSize } from "../store/slices/ui";
import { selectExport } from "../store/slices/auth";
import { useSaveSearchMutation } from "../store/apis/save";
import { mapValuesToSaveData } from "../utils";

// Define an interface for the row data structure
interface SpectrumPricingRow {
  id: number;
  operator?: {
    id?: number;
    title?: string;
    country?: {
      title?: string;
    };
  };
  country?: {
    title?: string;
  };
  licence?: {
    title?: string;
  };
  frequency_bands?: {
    title?: string;
    range?: string;
    suffix?: string;
  }[];
  completed_year?: number;
  completed_month?: number;
  price_total?: number;
  price_population?: number;
  allocated_bandwith?: number;
  children?: SpectrumPricingRow[];
  isExpanded?: boolean;
}

const SpectrumPricingSearch: React.FC = () => {
  const dispatch = useDispatch();
  const { dense, fontSize, exportFields } = useSelector(selectUiState);
  const { sortDesc, sortBy, rowsPerPage, page, values, lastSaved } = useSelector(selectSpectrumPricingSlice);
  const exportEnabled = useSelector(selectExport);
  const [saveSearch, { isLoading: isSaving }] = useSaveSearchMutation();
  const [trigger, { data: download, isLoading: isExporting }] = useLazyExportSpectrumPricingQuery();
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const {
    data = { results: [], total: 0 },
    isLoading,
    isFetching,
    isUninitialized,
  } = useGetSpectrumPricingQuery({
    rowsPerPage,
    page,
    values,
    sortBy,
    sortDirection: sortDesc ? "desc" : "asc",
  });

  const { results = [], total = 0 } = data;
  const pages = Math.ceil(total / rowsPerPage);
  const loading = isUninitialized || isLoading || isFetching;

  console.log(results)

  // Process the results to include expanded state
  const processedResults = React.useMemo(() => {
    const processRow = (row: SpectrumPricingRow): SpectrumPricingRow[] => {
      const hasChildren = row.children && row.children.length > 0;
      const isExpanded = hasChildren && expandedRows.has(row.id);

      const processedRow = {
        ...row,
        isExpanded
      };

      if (hasChildren && isExpanded && row.children) {
        return [processedRow, ...row.children];
      }

      return [processedRow];
    };

    return results.flatMap(processRow);
  }, [results, expandedRows]);

  const handleRowClick = (isTotal: boolean, rowData: any) => {
    const row = rowData as SpectrumPricingRow;
    if (row.children && row.children.length > 0) {
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        if (newSet.has(row.id)) {
          newSet.delete(row.id);
        } else {
          newSet.add(row.id);
        }
        return newSet;
      });
    }
  };

  const handleSave = () => {
    dispatch(setSavedValues(values));
    saveSearch({
      sectionId: 7,
      data: mapValuesToSaveData(7, values),
    });
  };

  const renderButtons = () => (
    <Box paddingY={4}>
      <Grid2 container spacing={4} alignItems="center">
        <Grid2>
          <SaveSearch disabled={isEqual(values, lastSaved)} loading={isSaving} onClick={handleSave} />
        </Grid2>
{/*         <Grid2>
          <ExportCsv
            disabled={exportEnabled ? false : total > 2000}
            source={download}
            loading={isExporting}
            onClick={() => trigger({ values: values, fields: exportFields })}
          />
        </Grid2> */}
      </Grid2>
    </Box>
  );

  return (
    <>
      <Helmet title="Spectrum Pricing - GSA GAMBoD" />
      <FilterNav />
      <PageHeading
        title={allRoutes.spectrum_pricing.label}
        icon={allRoutes.spectrum_pricing.icon}
        color={allRoutes.spectrum_pricing.color}
        section="spectrum"
        inset
      >
        <Breadcrumbs aria-label="Breadcrumb">
          <Link href={allRoutes.dashboard.path}>Dashboard</Link>
          <Link href={allRoutes.spectrum_pricing.path}>Spectrum Pricing</Link>
          <Typography>Search</Typography>
        </Breadcrumbs>
      </PageHeading>
      <ContentWrapper inset>
        <Grid2 container justifyContent="space-between" alignItems="center">
          <Grid2 size="grow">
            <Box paddingY={10}>
              <Grid2 container spacing={8} alignItems="flex-end" wrap="nowrap">
                <Grid2 sx={{ display: { xs: "none", md: "block" } }}>
                  <Typography variant="h4">Your Results</Typography>
                </Grid2>
                <Grid2>
                  <Typography variant="subtitle2" gutterBottom>
                    Returned results
                  </Typography>
                  <Typography variant="h4">{loading ? "--" : `${total.toLocaleString()} Spectrum Pricings`}</Typography>
                </Grid2>
              </Grid2>
            </Box>
          </Grid2>
          <Grid2 sx={{ display: { xs: "none", md: "block" } }}>{renderButtons()}</Grid2>
        </Grid2>
      </ContentWrapper>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <ContentWrapper inset>
          <Divider />
          {renderButtons()}
        </ContentWrapper>
      </Box>
      <Divider />
      <AppLoader loading={loading} />
      <ContentWrapper body inset>
        {!loading && !results.length ? (
          <NoResults />
        ) : (
          <TableAdvanced
            title={`GAMBoD Spectrum Pricing Search - ${total.toLocaleString()} results, page ${page}`}
            dense={dense}
            rows={processedResults}
            fontSize={fontSize}
            cellsMap={spectrumPricingCells(sortBy, sortDesc)}
            page={page}
            pages={pages}
            rowsPerPage={rowsPerPage}
            loading={loading}
            setRowsPerPage={value => {
              dispatch(setRowsPerPage(value));
            }}
            setPage={value => {
              dispatch(setPage(value));
            }}
            setFontSize={value => {
              dispatch(setFontSize(value));
            }}
            setDense={value => {
              dispatch(setDense(value));
            }}
            onOrder={value => {
              dispatch(setSortBy(value));
            }}
            onRowClick={handleRowClick}
          />
        )}
      </ContentWrapper>
    </>
  );
};

export default SpectrumPricingSearch; 